<template>
    <div id="comment-panel" class="col p-0 h-100">
        <div id="comment-header" class="d-flex justify-content-between bg-white align-items-center pt-2 pr-3 drag-handle">
            <span class="clickable d-block px-3" @click="$emit('closeDiscussion')">
                <fa-icon :icon="['fal', 'chevron-left']" />
            </span>
            <strong>{{ chat.label }}</strong>
        </div>
        <div id="comment-users" class="d-flex justify-content-center bg-white pt-1 pb-2 shadow-sm drag-handle">
            <span v-for="user in chat.users" :key="'u' + user.id" class="mx-1">
                <avatar :user="user" />
            </span>
        </div>
        <div ref="comments" id="comments" class="bg-light p-3" :style="'bottom: ' + commentHeight + 'px'">
            <loading v-if="loading" />
            <div class="comment mt-4" v-for="comment in orderedComments" :id="'c' + comment.id" :key="'c' + comment.id" :class="auth.id === comment.author.id ? 'my-comment' : ''">
                <div class="avatar d-flex flex-column text-center">
                    <avatar :user="comment.author" :height="40" />
                    <span class="author text-uppercase"><strong>{{ comment.author.first }}</strong></span>
                </div>
                <div class="content position-relative bg-white shadow-sm py-2 px-3">
                    <div v-html="comment.comment"></div>
                    <div class="mt-2 d-flex justify-content-between align-items-center">
                      <small><b>{{ comment.created_at | luxon:diffForHumans }}</b></small>
                      <fa-icon v-if="comment.state === 'sent'" class="text-success" :icon="['fas', 'check-circle']" />
                      <fa-icon v-else-if="comment.state === 'failed'" class="text-warning" :icon="['fas', 'times']" />
                      <fa-icon v-else-if="comment.state === 'pending'" :icon="['fal', 'atom-alt']" class="text-info loading fa-spin"></fa-icon>
                    </div>
                    <div v-if="false && comment.author.id === auth.id" class="position-absolute comment-control">
                        <bs-action :icon="['fal', 'pencil']" class="btn-info mr-1"></bs-action>
                        <bs-action :icon="['fal', 'trash']" class="btn-danger" @click="destroy(comment)"></bs-action>
                    </div>
                </div>
            </div>
        </div>
        <div id="comment" class="border-top bg-white w-100" ref="commentBox">
            <comment-field @comment="comment" :initContent="draftComment" @submit="send" />
        </div>
        <bs-modal :name="'destroy-' + $options.name" :label="$t('labels.destroy_' + camelToSnake(singular($options.name)))">
            <bs-form :destroy="$t('labels.destroy')" @submit="destruction">
                {{ $t('messages.delete', { resource: camelToSpace(singular($options.name)) }) }}
            </bs-form>
        </bs-modal>
    </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'Comments',
  data () {
    return {
      loading: false,
      activeId: null,
      scrolledToTop: false,
      scrolledToBottom: false,
      commentHeight: 50,
      comments: {},
      draftComment: null,
      handle: null
    }
  },
  computed: {
    ...mapState({
      auth: state => state.auth,
      submitting: state => state.submitting,
      chat: state => state.chatActive
    }),
    orderedComments () {
      if (this.comments) {
        return this._.orderBy(this.comments, 'created_at')
      } else return []
    }
  },
  mounted () {
    this.scroll()
    this.index()
    setTimeout(() => {
      this.commentHeight = this.$refs.commentBox ? this.$refs.commentBox.clientHeight : 50
    }, 500)
    this.handle = setInterval(() => {
      if (this.scrolledToBottom) {
        this.index('fresh')
      }
    }, 5000)
  },
  beforeDestroy () {
    clearInterval(this.handle)
  },
  watch: {
    chat (newVal, oldVal) {
      if (newVal && newVal.id) {
        if (oldVal && oldVal.id && oldVal.id === newVal.id) { } else {
          this.index()
        }
      }
    }
  },
  methods: {
    comment (content) {
      this.$store.commit('processComment', { id: this.chat.id, comment: content })
    },
    send (content) {
      this.$store.dispatch('submitComment', { id: this.chat.id, content: content })
        .then(() => {
          this.draftComment = null
          this.scrollToBottom()
        })
    },
    index (type = null) {
      this.draftComment = this.$store.getters.getDiscussionDraftComment(this.chat.id)
      if (!type) {
        const position = this.$store.getters.getDiscussionPosition(this.chat.id)
        // If we get back 'bottom' as the value then the getter couldn't find any comments in this discussion, so we
        // need to ask the server for comments
        if (position === 'bottom') {
          type = 'poll'

          // Otherwise we got a saved position, so we can load the comments and then scroll to the saved position
        } else {
          this.comments = this.$store.getters.getComments(this.chat.id)

          // Give the DOM a split second to load before setting the scroll position
          setTimeout(() => {
            const elem = this.$refs.comments
            elem.scrollTo(0, position)
          }, 100)
        }
      }

      // If we are polling the server
      if (type === 'poll') {
        this.loading = true
        this.$store.dispatch('indexComments', this.chat).then(() => {
          let idToView = false
          if (this.orderedComments.length > 0) {
            idToView = this.orderedComments[0].id
          }
          this.loading = false
          this.comments = this.$store.getters.getComments(this.chat.id)
          setTimeout(() => {
            if (idToView) {
              const elem = document.getElementById('c' + idToView)
              elem.scrollIntoView()
            } else {
              this.scrollToBottom()
            }
          }, 100)
        })
      }

      // If we are getting fresh data
      if (type === 'fresh') {
        this.$store.dispatch('freshComments', this.chat).then(() => {
          this.comments = this.$store.getters.getComments(this.chat.id)
          setTimeout(() => {
            this.scrollToBottom()
          }, 100)
        })
      }
    },
    create () {
      this.$store.commit('axiosError', null)
      this.$root.clearData(this.resource)
      this.$modal.show('create-' + this.$options.name)
    },
    destroy (o) {
      this.activeId = o.id
      this.$modal.show('destroy-' + this.$options.name)
    },
    update (o) {
      this.$store.commit('axiosError', null)
      this.resource = JSON.parse(JSON.stringify(o))
      this.$modal.show('create-' + this.$options.name)
    },
    destruction () {
      this.$store.dispatch('destroyComment', { id: this.activeId, discussion: this.chat.id }).then(() => {
        this.comments = this.$store.getters.getComments(this.chat.id)
        this.$modal.hide('destroy-' + this.$options.name)
      })
    },
    scroll () {
      const elem = this.$refs.comments
      elem.onscroll = () => {
        this.$store.commit('processScollPosition', { id: this.chat.id, position: elem.scrollTop })
        if (elem.scrollTop === 0) {
          this.scrolledToTop = true
          this.index('poll')
        } else if ((elem.clientHeight + elem.scrollTop) >= elem.scrollHeight - 50) {
          this.scrolledToBottom = true
        } else {
          this.scrolledToTop = false
          this.scrolledToBottom = false
        }
      }
    },
    scrollToBottom () {
      const elem = this.$refs.comments
      elem.scrollTo(0, elem.scrollHeight)
    }

  }

}
</script>

<style lang="scss">
    #comment-panel {
        position: relative;

        #comment-header {
            line-height: 32px;
        }

        #comment-users {
            position: relative;
            z-index: 1;
        }

        #comments {
            position: absolute;
            top: 84px;
            bottom: 50px;
            left: 0;
            right: 0;
            overflow-y: scroll;

            .comment {
                display: flex;
                font-size: 0.875rem;

                .avatar {
                    margin-right: 0.25rem;
                }

                .author {
                    font-size: 0.75rem;
                }

                &.my-comment {
                    flex-direction: row-reverse;

                    .avatar {
                        margin-right: 0;
                        margin-left: 0.25rem;
                    }
                }

                .content {
                    border-radius: 5px;
                    font-weight: 400;
                    word-break: break-word;
                    min-width: 160px;

                    .comment-control {
                        right: 5px;
                        bottom: 5px;
                    }
                }

            }

            &::-webkit-scrollbar {
                width: 4px;
                height: 10px;
            }
            &::-webkit-scrollbar-track {
                border-radius: 10px;
                border: none;
            }
            &::-webkit-scrollbar-thumb {
                border-radius: 3px;
                background: var(--gray);
            }

            p {
                margin-bottom: 0.4rem;
            }
        }

        #comment {
            position: absolute;
            bottom: 0;
            font-size: 0.875rem;

            .editor__content {
                max-height: 200px;
                min-height: 52px;
                overflow-Y: scroll;

                &::-webkit-scrollbar {
                    width: 4px;
                    height: 10px;
                }
                &::-webkit-scrollbar-track {
                    border-radius: 10px;
                    border: none;
                }
                &::-webkit-scrollbar-thumb {
                    border-radius: 3px;
                    background: var(--gray);
                }
            }

            p {
                margin-bottom: 0.4rem;
            }
        }
    }
</style>
